import React, { useState } from 'react';
import { Button, Snackbar, Alert, Tooltip } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContact, getContacts } from '../api/contacts';
import { parseFromAddress } from '../utils/emailAddress';

interface AddSenderContactButtonProps {
  fromAddress: string;
}

export default function AddSenderContactButton({ fromAddress }: AddSenderContactButtonProps) {
  const qc = useQueryClient();
  const parsed = parseFromAddress(fromAddress);
  const [snack, setSnack] = useState<{ severity: 'success' | 'error' | 'info'; msg: string } | null>(
    null
  );

  const { data: contacts } = useQuery({
    queryKey: ['contacts', 'lookup', parsed.email],
    queryFn: () => getContacts({ search: parsed.email, page_size: 50 }),
    enabled: Boolean(parsed.email),
  });

  const alreadyExists = Boolean(
    contacts?.results?.some((c) => c.email.toLowerCase() === parsed.email.toLowerCase())
  );

  const mutation = useMutation({
    mutationFn: () =>
      createContact({
        email: parsed.email,
        first_name: parsed.first_name,
        last_name: parsed.last_name,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      setSnack({ severity: 'success', msg: `${parsed.email} added to address book.` });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { email?: string[] | string; detail?: string } } })?.response
          ?.data;
      const msg =
        (Array.isArray(detail?.email) ? detail?.email[0] : detail?.email) ||
        detail?.detail ||
        'Could not add contact.';
      setSnack({ severity: 'error', msg: String(msg) });
    },
  });

  if (!parsed.email) return null;

  return (
    <>
      <Tooltip title={alreadyExists ? 'Already in address book' : 'Add sender to address book'}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon />}
            disabled={alreadyExists || mutation.isPending}
            onClick={() => mutation.mutate()}
            sx={{ mt: 0.5 }}
          >
            {alreadyExists ? 'In address book' : 'Add to address book'}
          </Button>
        </span>
      </Tooltip>
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
